import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import './Catalogue.css';

const TAB_TO_DIMENSION = {
  domains: 'Domain',
  problem: 'Problem Space',
  solution: 'Solution Space'
};



const createEmptyExpandedMap = () => ({
  domains: new Set(),
  problem: new Set(),
  solution: new Set()
});

const parseExpandedFromStorage = () => {
  try {
    const saved = sessionStorage.getItem('catalogue_expandedIds');
    if (!saved) return createEmptyExpandedMap();
    const parsed = JSON.parse(saved);
    if (Array.isArray(parsed)) {
      return {
        domains: new Set(parsed),
        problem: new Set(),
        solution: new Set()
      };
    }
    return {
      domains: new Set(parsed.domains || []),
      problem: new Set(parsed.problem || []),
      solution: new Set(parsed.solution || [])
    };
  } catch (err) {
    console.warn('Failed to parse stored expanded IDs', err);
    return createEmptyExpandedMap();
  }
};

const TagList = ({ items, type }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="tag-list">
      {items.map((item, idx) => (
        <span key={idx} className={`tag ${type}`}>
          {item}
        </span>
      ))}
    </div>
  );
};

const PatentPage = React.forwardRef(({ patent, position, viewMode, onNavigate }, ref) => {
  const classes = ['catalogue-page'];
  if (viewMode === 'two-page') {
    classes.push(position === 'left' ? 'page-left' : 'page-right');
  } else {
    classes.push('page-single');
  }
  if (!patent) {
    classes.push('empty-page');
  }

  return (
    <div className={classes.join(' ')} ref={ref}>
      {!patent ? (
        <div className="empty-page-placeholder">
          <h3>Keep paging</h3>
          <p>Select another entry from the TOC to load fresh patents.</p>
        </div>
      ) : (
        <>
          <div className="page-header">
            <div className="patent-number">
              US Patent Number{' '}
              <a
                href={`https://image-ppubs.uspto.gov/dirsearch-public/print/downloadPdf/${patent.patent_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="patent-link"
              >
                {patent.patent_id}
              </a>
            </div>
            <h2 className="page-title">
              <span
                className="page-title-link"
                onClick={() => onNavigate && onNavigate(patent.patent_id)}
              >
                {patent.title}
              </span>
            </h2>
          </div>

          <div className="page-content">
            <section className="page-section">
              <h3 className="section-label">Plain English Summary</h3>
              <p className="section-content summary">{patent.plain_english_summary}</p>
            </section>

            <section className="page-section">
              <h3 className="section-label">Technical Description</h3>
              <p className="section-content">{patent.technical_description}</p>
            </section>

            <section className="page-section">
              <h3 className="section-label">Domains</h3>
              <TagList items={patent.domains} type="domain" />
            </section>

            <section className="page-section">
              <h3 className="section-label">Problem Space</h3>
              <TagList items={patent.problem_space} type="problem" />
            </section>

            <section className="page-section">
              <h3 className="section-label">Solution Space</h3>
              <TagList items={patent.solution_space} type="solution" />
            </section>

            <section className="page-section">
              <h3 className="section-label">IPC Codes</h3>
              <TagList items={patent.ipc_codes} type="ipc" />
            </section>

            <button className="view-details-btn" onClick={() => onNavigate && onNavigate(patent.patent_id)}>
              View Full Details
            </button>
          </div>
        </>
      )}
    </div>
  );
});

PatentPage.displayName = 'PatentPage';

const TocTreeNode = ({
  cluster,
  type,
  onSelect,
  showEmpty,
  path = [],
  expandedIds,
  onToggle,
  selectable = false,
  selectedClusterId = null,
  onSelectCluster = () => {}
}) => {
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(false);

  // For CPC, level 5 is leaf. For others too.
  const isLeaf = cluster.level >= 5;
  const isEmpty = cluster.patent_count === 0;
  const isExpanded = expandedIds.has(cluster.cluster_id);

  const fetchChildren = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        type: type,
        level: (cluster.level + 1).toString(),
        parent_id: cluster.cluster_id,
        include_empty: showEmpty.toString()
      });
      const response = await axios.get(`${config.apiBaseUrl}/hierarchical-clusters?${params.toString()}`);
      setChildren(response.data.clusters);
    } catch (err) {
      console.error('Error fetching children:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isExpanded) {
      fetchChildren();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEmpty, isExpanded]);

  const handleToggle = async (e) => {
    e.stopPropagation();
    if (isLeaf) {
      if (!isEmpty) {
        onSelect(cluster, [...path, cluster.label]);
      }
      return;
    }

    onToggle(cluster.cluster_id);
  };

  // If we are hiding empty nodes and this one is empty, don't render (though parent shouldn't have fetched it)
  // But if we are showing empty nodes, render with 'empty' class
  
  // Display full label including CPC class name for domains
  const displayLabel = cluster.label;

  const fullPath = [...path, cluster.label];
  const selectableNode = selectable && !isEmpty;

  return (
    <div className="toc-tree-node">
      <div 
        className={`toc-tree-label ${isLeaf ? 'leaf' : 'folder'} ${isExpanded ? 'expanded' : ''} ${isEmpty ? 'empty' : ''}`}
        onClick={handleToggle}
        style={{ cursor: isEmpty && isLeaf ? 'default' : 'pointer' }}
      >
        {selectable && (
          <label
            className={`toc-select-control ${selectableNode ? '' : 'disabled'}`}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <input
              type="radio"
              name={`toc-select-${type}`}
              disabled={!selectableNode}
              checked={selectedClusterId === cluster.cluster_id}
              onChange={(e) => {
                e.stopPropagation();
                if (!selectableNode) return;
                onSelectCluster(cluster, fullPath);
              }}
            />
            <span className="toc-radio" />
          </label>
        )}
        <span className="toc-icon">
          {isLeaf ? '📄' : (isExpanded ? '📂' : '📁')}
        </span>
        <span className="toc-text">
          {displayLabel}
          <span className="toc-count">
            ({cluster.patent_count} patents)
          </span>
        </span>
      </div>
      
      {isExpanded && (
        <div className="toc-tree-children">
          {loading ? (
            <div className="toc-loading">
              <div className="loading-bar-container">
                <div className="loading-bar"></div>
              </div>
            </div>
          ) : (
            children.map(child => (
              <TocTreeNode 
                key={child.cluster_id} 
                cluster={child} 
                type={type} 
                onSelect={onSelect}
                showEmpty={showEmpty}
                path={[...path, cluster.label]}
                expandedIds={expandedIds}
                onToggle={onToggle}
                selectable={selectable}
                selectedClusterId={selectedClusterId}
                onSelectCluster={onSelectCluster}
              />
            ))
          )}
          {!loading && children.length === 0 && (
            <div className="toc-empty">No subcategories</div>
          )}
        </div>
      )}
    </div>
  );
};

function Catalogue() {
  const navigate = useNavigate();
  const [browsePatents, setBrowsePatents] = useState([]); // Stores the paginated list for "Browse All"
  const [displayPatents, setDisplayPatents] = useState([]);
  const [index, setIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [viewMode, setViewMode] = useState('two-page');
  const [displayMode, setDisplayMode] = useState('patents'); // 'patents', 'toc', 'index'
  const [tocTab, setTocTab] = useState('domains'); // 'domains', 'problem', 'solution'
  const [searchIndex, setSearchIndex] = useState('');
  const [filter, setFilter] = useState(null); // {type: 'toc'|'index', label: string, patentIds: []} // Default: hide empty (toggle ON means hide, so false means hide? User said "toggle on (default) -> hidden")
  // Let's interpret: Toggle Switch ON = "Hide Empty" = true. Default is ON.
  // Wait, usually toggle ON means "Show Empty".
  // User said: "When the toggle is on (default), unpopulated domains are hidden."
  // So Toggle Label: "Hide Empty Domains". Checked = Hidden. Default Checked.
  // Or Toggle Label: "Show Empty Domains". Unchecked = Hidden. Default Unchecked.
  // "When the toggle is on (default), unpopulated domains are hidden." -> This implies the switch represents "Hiding".
  // Let's call the state `hideEmpty` and default it to `true`.
  const [hideEmpty, setHideEmpty] = useState(() => {
    const saved = sessionStorage.getItem('catalogue_hideEmpty');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [apiPage, setApiPage] = useState(1);
  const [totalPatents, setTotalPatents] = useState(null); // Total count from API
  
  // Hierarchical TOC state
  const [tocClusters, setTocClusters] = useState([]);
  const [tocLoadingClusters, setTocLoadingClusters] = useState(false);
  const [expandedIdsMap, setExpandedIdsMap] = useState(() => parseExpandedFromStorage());

  const [dimensionFilters, setDimensionFilters] = useState({});
  const [selectedClusters, setSelectedClusters] = useState({
    domains: null,
    problem: null,
    solution: null
  });
  const [pageTurnDirection, setPageTurnDirection] = useState(null);
  const pageTurnTimerRef = useRef(null);
  const expandedIds = expandedIdsMap[tocTab] || new Set();
  const allowCategorySelection = tocTab === 'domains';

  const triggerPageTurnAnimation = (direction) => {
    if (!direction) return;
    setPageTurnDirection(direction);
    if (pageTurnTimerRef.current) {
      clearTimeout(pageTurnTimerRef.current);
    }
    pageTurnTimerRef.current = setTimeout(() => {
      setPageTurnDirection(null);
    }, 600);
  };

  const goToPage = (targetIndex, totalLength = null) => {
    const availableLength = totalLength !== null ? totalLength : displayPatents.length;
    if (availableLength <= 0) {
      setCurrentPage(0);
      return;
    }

    const maxIndex = Math.max(availableLength - 1, 0);
    const clamped = Math.max(0, Math.min(targetIndex, maxIndex));

    if (viewMode === 'two-page') {
      const normalized = clamped - (clamped % 2);
      const direction = normalized > currentPage ? 'forward' : normalized < currentPage ? 'backward' : null;
      setCurrentPage(normalized);
      triggerPageTurnAnimation(direction);
    } else {
      const direction = clamped > currentPage ? 'forward' : clamped < currentPage ? 'backward' : null;
      setCurrentPage(clamped);
      triggerPageTurnAnimation(direction);
    }
  };

  const resetToFirstPage = (totalLength = null) => {
    goToPage(0, totalLength);
  };

  // Initialize state from sessionStorage if available
  useEffect(() => {
    const savedDisplayMode = sessionStorage.getItem('catalogue_displayMode');
    if (savedDisplayMode) setDisplayMode(savedDisplayMode);

    const savedTocTab = sessionStorage.getItem('catalogue_tocTab');
    if (savedTocTab) setTocTab(savedTocTab);

    const savedFilter = sessionStorage.getItem('catalogue_filter');
    if (savedFilter) setFilter(JSON.parse(savedFilter));
    
    const savedDimensionFilters = sessionStorage.getItem('catalogue_dimensionFilters');
    if (savedDimensionFilters) setDimensionFilters(JSON.parse(savedDimensionFilters));
  }, []);

  // Persist state changes
  useEffect(() => {
    sessionStorage.setItem('catalogue_displayMode', displayMode);
  }, [displayMode]);

  useEffect(() => {
    sessionStorage.setItem('catalogue_tocTab', tocTab);
  }, [tocTab]);

  useEffect(() => {
    if (filter) {
      sessionStorage.setItem('catalogue_filter', JSON.stringify(filter));
    } else {
      sessionStorage.removeItem('catalogue_filter');
    }
  }, [filter]);
  
  useEffect(() => {
    sessionStorage.setItem('catalogue_dimensionFilters', JSON.stringify(dimensionFilters));
  }, [dimensionFilters]);

  useEffect(() => {
    sessionStorage.setItem('catalogue_hideEmpty', JSON.stringify(hideEmpty));
  }, [hideEmpty]);

  useEffect(() => {
    const serialized = {
      domains: Array.from(expandedIdsMap.domains || []),
      problem: Array.from(expandedIdsMap.problem || []),
      solution: Array.from(expandedIdsMap.solution || [])
    };
    sessionStorage.setItem('catalogue_expandedIds', JSON.stringify(serialized));
  }, [expandedIdsMap]);

  useEffect(() => {
    const maxIndex = Math.max(displayPatents.length - 1, 0);
    setCurrentPage(prev => {
      if (prev > maxIndex) {
        const normalized = viewMode === 'two-page' ? maxIndex - (maxIndex % 2) : maxIndex;
        return Math.max(normalized, 0);
      }
      return prev;
    });
  }, [displayPatents.length, viewMode]);

  useEffect(() => {
    if (viewMode === 'two-page' && currentPage % 2 !== 0) {
      setCurrentPage(prev => Math.max(prev - 1, 0));
    }
  }, [viewMode, currentPage]);

  useEffect(() => {
    return () => {
      if (pageTurnTimerRef.current) {
        clearTimeout(pageTurnTimerRef.current);
      }
    };
  }, []);

  const handleToggleNode = (clusterId) => {
    setExpandedIdsMap(prev => {
      const updated = { ...prev };
      const currentSet = new Set(updated[tocTab] || []);
      if (currentSet.has(clusterId)) {
        currentSet.delete(clusterId);
      } else {
        currentSet.add(clusterId);
      }
      updated[tocTab] = currentSet;
      return updated;
    });
  };

  const handleClusterRadioSelect = (cluster, pathLabels) => {
    setSelectedClusters(prev => ({
      ...prev,
      [tocTab]: {
        cluster,
        pathLabels,
        tab: tocTab
      }
    }));
  };

  const fetchPatentsForCluster = async (cluster, path = []) => {
    if (!cluster) return;
    const activeTab = cluster.type || tocTab;
    setTocLoadingClusters(true);
    const perPage = 500;
    try {
      const baseParams = {
        type: activeTab,
        cluster_id: cluster.cluster_id,
        per_page: perPage
      };

      const firstResponse = await axios.get(`${config.apiBaseUrl}/hierarchical-clusters/patents`, {
        params: { ...baseParams, page: 1 }
      });
      let collectedPatents = firstResponse.data.patents || [];
      const totalPages = firstResponse.data.pages || 1;

      for (let nextPage = 2; nextPage <= totalPages; nextPage += 1) {
        const nextResponse = await axios.get(`${config.apiBaseUrl}/hierarchical-clusters/patents`, {
          params: { ...baseParams, page: nextPage }
        });
        if (nextResponse.data?.patents?.length) {
          collectedPatents = collectedPatents.concat(nextResponse.data.patents);
        }
      }

      const patentIds = collectedPatents.map(p => p.patent_id);
      const formatLabel = (label) => (activeTab === 'domains' && label.includes(' - ') ? label.split(' - ')[0] : label);
      const formattedPath = path.map(formatLabel);
      const fullLabel = formattedPath.join(' > ') || formatLabel(cluster.label);
      const dimensionLabel = TAB_TO_DIMENSION[activeTab] || 'Category';

      applyFilter('toc', fullLabel, patentIds, collectedPatents, dimensionLabel, activeTab);
    } catch (err) {
      console.error('Patent fetch error:', err);
    } finally {
      setTocLoadingClusters(false);
    }
  };

  useEffect(() => {
    const fetchCatalogueData = async () => {
      setLoading(true);
      setError(null);

      try {
        const indexResponse = await axios.get(`${config.apiBaseUrl}/catalogue/index`);

        setIndex(indexResponse.data.index);
        
        // Fetch first page of patents
        const patentsResponse = await axios.get(`${config.apiBaseUrl}/patents?page=1&per_page=50`);
        setBrowsePatents(patentsResponse.data.patents);
        setDisplayPatents(patentsResponse.data.patents);
        setTotalPatents(patentsResponse.data.total); // Store total count
        setApiPage(1);
        
      } catch (err) {
        setError('Error loading catalogue. Please try again.');
        console.error('Catalogue error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCatalogueData();
  }, []);

  useEffect(() => {
    if (displayMode === 'toc') {
      fetchTocClusters(1, null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tocTab, displayMode, hideEmpty]); // Refetch when hideEmpty changes

  const applyFilter = async (filterType, label, patentIds, providedPatents = null, dimension = null, activeTab = null, switchMode = true) => {
    let filtered = [];
    if (providedPatents) {
      filtered = providedPatents;
    } else {
      // Fetch patents by ID
      try {
        // If we have many IDs, we might need to chunk this or use POST, but GET with params is usually ok for reasonable length
        // For index, it might be small.
        const response = await axios.get(`${config.apiBaseUrl}/patents`, {
          params: { patent_ids: patentIds, per_page: 1000 }
        });
        filtered = response.data.patents;
      } catch (err) {
        console.error('Error fetching filtered patents:', err);
      }
    }
    setDisplayPatents(filtered);
    setTotalPatents(filtered.length); // Update total to filtered count
    const newFilter = { type: filterType, label, patentIds, dimension, tab: activeTab };
    setFilter(newFilter);
    
    // Save to dimensionFilters if it's a TOC filter
    if (filterType === 'toc' && dimension && activeTab) {
        setDimensionFilters(prev => ({
            ...prev,
            [activeTab]: newFilter
        }));
    }
    
    if (switchMode) setDisplayMode('patents');
    resetToFirstPage(filtered.length);
  };

  const clearFilter = () => {
    setDisplayPatents(browsePatents);
    setFilter(null);
    // Restore the original total count
    axios.get(`${config.apiBaseUrl}/patents?page=1&per_page=1`)
      .then(res => setTotalPatents(res.data.total))
      .catch(err => console.error('Error fetching total:', err));
    resetToFirstPage(browsePatents.length);
  };

  const handleNavigateToPatent = (patentId) => {
    navigate(`/patent/${patentId}`);
  };

  const collapseCurrentDimension = () => {
    setExpandedIdsMap(prev => ({
      ...prev,
      [tocTab]: new Set()
    }));

    setDimensionFilters(prev => {
      const updated = { ...prev };
      delete updated[tocTab];
      return updated;
    });

    setSelectedClusters(prev => ({
      ...prev,
      [tocTab]: null
    }));

    if (filter?.type === 'toc' && filter?.dimension === TAB_TO_DIMENSION[tocTab]) {
      clearFilter();
    }
  };

  const fetchTocClusters = async (level, parentId = null) => {
    setTocLoadingClusters(true);
    try {
      const params = new URLSearchParams({
        type: tocTab,
        level: level.toString(),
        include_empty: (!hideEmpty).toString() // If hideEmpty is true, include_empty is false
      });
      
      if (parentId) {
        params.append('parent_id', parentId.toString());
      }

      const response = await axios.get(`${config.apiBaseUrl}/hierarchical-clusters?${params.toString()}`);
      setTocClusters(response.data.clusters);
    } catch (err) {
      console.error('TOC cluster error:', err);
    } finally {
      setTocLoadingClusters(false);
    }
  };

  const handleTocNodeSelect = async (cluster, path = []) => {
    fetchPatentsForCluster(cluster, path);
  };

  const handleViewSelectedPatents = () => {
    const selection = selectedClusters[tocTab];
    if (!selection) return;
    fetchPatentsForCluster(selection.cluster, selection.pathLabels);
  };

  const handleIndexClick = (entry) => {
    const patentIds = entry.patents.map(p => p.patent_id);
    applyFilter('index', entry.keyword, patentIds);
  };

  const nextPage = async () => {
    const step = viewMode === 'two-page' ? 2 : 1;
    const desiredIndex = currentPage + step;

    if (!filter && desiredIndex >= displayPatents.length) {
      try {
        const nextPageNum = apiPage + 1;
        const response = await axios.get(`${config.apiBaseUrl}/patents?page=${nextPageNum}&per_page=50`);
        
        if (response.data.patents.length > 0) {
          const newPatents = [...browsePatents, ...response.data.patents];
          setBrowsePatents(newPatents);
          setDisplayPatents(newPatents);
          setApiPage(nextPageNum);
          goToPage(desiredIndex, newPatents.length);
        }
      } catch (err) {
        console.error('Error fetching more patents:', err);
      }
    } else {
      goToPage(desiredIndex);
    }
  };

  const prevPage = () => {
    const step = viewMode === 'two-page' ? 2 : 1;
    goToPage(currentPage - step);
  };

  const renderToc = () => {
    // Load clusters when switching to TOC mode
    if (displayMode === 'toc' && tocClusters.length === 0 && !tocLoadingClusters) {
      fetchTocClusters(1, null);
    }

    const handleTabChange = (tab) => {
      setTocTab(tab);
      setTocClusters([]);
      
      // Restore filter for this tab if available
      const savedFilter = dimensionFilters[tab];
      if (savedFilter) {
          applyFilter(savedFilter.type, savedFilter.label, savedFilter.patentIds, null, savedFilter.dimension, tab, false);
      } else {
          // No saved filter for this dimension, clear filter (show all)
          clearFilter();
      }
    };

    const formatLabelForDisplay = (label) => (tocTab === 'domains' && label.includes(' - ') ? label.split(' - ')[0] : label);
    const activeSelection = selectedClusters[tocTab];
    const selectionLabel = activeSelection?.pathLabels?.length
      ? activeSelection.pathLabels.map(formatLabelForDisplay).join(' > ')
      : 'No category selected';

    return (
      <div className="toc-tree-container">
        <div className="toc-tabs-row">
          <div className="toc-tabs">
            <button 
              className={`toc-tab ${tocTab === 'domains' ? 'active' : ''}`}
              onClick={() => handleTabChange('domains')}
            >
              Domains
            </button>
            <button 
              className={`toc-tab ${tocTab === 'problem' ? 'active' : ''}`}
              onClick={() => handleTabChange('problem')}
            >
              Problem Spaces
            </button>
            <button 
              className={`toc-tab ${tocTab === 'solution' ? 'active' : ''}`}
              onClick={() => handleTabChange('solution')}
            >
              Solution Spaces
            </button>
          </div>

          <button 
            type="button"
            className="collapse-all-btn"
            onClick={collapseCurrentDimension}
          >
            Collapse {TAB_TO_DIMENSION[tocTab]}
          </button>
        </div>

        {allowCategorySelection && (
          <div className="toc-selection-toolbar">
            <div className="selected-category-display">
              <span className="selected-label">Selected:</span>
              <span className="selected-value">{selectionLabel}</span>
            </div>
            <button
              type="button"
              className="btn btn-primary view-selected-btn"
              disabled={!activeSelection || tocLoadingClusters}
              onClick={handleViewSelectedPatents}
            >
              View Patents
            </button>
          </div>
        )}

        {tocTab === 'domains' && (
          <div className="toggle-container">
            <label className="toggle-switch">
              <input 
                type="checkbox" 
                checked={hideEmpty} 
                onChange={() => setHideEmpty(!hideEmpty)} 
              />
              <span className="toggle-slider"></span>
            </label>
            <span>Hide empty domains</span>
          </div>
        )}

        {tocLoadingClusters && tocClusters.length === 0 && (
          <div className="toc-loading">
            <div className="loading-bar-container">
              <div className="loading-bar"></div>
            </div>
          </div>
        )}

        <div className="toc-tree-root">
          {tocClusters.map((cluster) => (
            <TocTreeNode 
              key={cluster.cluster_id} 
              cluster={cluster} 
              type={tocTab} 
              onSelect={handleTocNodeSelect}
              showEmpty={!hideEmpty}
              path={[]}
              expandedIds={expandedIds}
              onToggle={handleToggleNode}
              selectable={allowCategorySelection}
              selectedClusterId={selectedClusters[tocTab]?.cluster?.cluster_id || null}
              onSelectCluster={handleClusterRadioSelect}
            />
          ))}
        </div>
      </div>
    );
  };

  const renderIndexCards = () => {
    if (!index) return null;

    const filteredIndex = searchIndex 
      ? index.filter(item => item.keyword.toLowerCase().includes(searchIndex.toLowerCase()))
      : index;

    // Group by first letter
    const grouped = {};
    filteredIndex.forEach(item => {
      const firstLetter = item.keyword[0].toUpperCase();
      if (!grouped[firstLetter]) {
        grouped[firstLetter] = [];
      }
      grouped[firstLetter].push(item);
    });

    const sortedLetters = Object.keys(grouped).sort();

    return (
      <div className="index-container">
        <div className="index-search">
          <input
            type="text"
            placeholder="Search index..."
            value={searchIndex}
            onChange={(e) => setSearchIndex(e.target.value)}
            className="index-search-input"
          />
        </div>

        <div className="index-alphabetical">
          {sortedLetters.map(letter => (
            <div key={letter} className="index-letter-section">
              <div className="index-letter-header">{letter}</div>
              <div className="index-cards-grid">
                {grouped[letter].map((entry, idx) => (
                  <div 
                    key={idx} 
                    className="index-card"
                    onClick={() => handleIndexClick(entry)}
                  >
                    <div className="index-card-keyword">{entry.keyword}</div>
                    <div className="index-card-count">{entry.count}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) return <div className="loading">Loading catalogue...</div>;
  if (error) return <div className="error">{error}</div>;

  const getPatentAt = (index) => (index >= 0 && index < displayPatents.length ? displayPatents[index] : null);
  const leftPatent = viewMode === 'two-page' ? getPatentAt(currentPage) : null;
  const rightPatent = viewMode === 'two-page' ? getPatentAt(currentPage + 1) : null;
  const currentPatent = viewMode === 'single-page' ? getPatentAt(currentPage) : null;
  const bookContainerClasses = [
    'catalogue-book',
    viewMode === 'single-page' ? 'single-page' : 'two-page',
    pageTurnDirection ? `book-turn-${pageTurnDirection}` : null
  ].filter(Boolean).join(' ');

  const renderFilterBreadcrumb = () => {
    if (!filter) return null;
    
    const isToc = filter.type === 'toc';
    const labelParts = isToc ? filter.label.split(' > ') : [filter.label];
    
    const handleBreadcrumbClick = (index) => {
      if (!isToc) {
        clearFilter();
        return;
      }
      
      // If clicking the last item, do nothing
      if (index === labelParts.length - 1) return;
      
      // If clicking a parent, we want to go back to TOC.
      // Ideally we would filter by that parent, but we don't have the patent IDs.
      // So we just go back to TOC view.
      // The TOC state (expanded nodes) is preserved, so the user can easily select the parent.
      clearFilter();
      setDisplayMode('toc');
    };

    return (
      <div className="filter-breadcrumb">
        <div className="breadcrumb-content">
          <span className="breadcrumb-prefix">
            Filtered by {filter.dimension || (isToc ? 'category' : 'keyword')}:
          </span>
          <div className="breadcrumb-trail">
            {labelParts.map((part, index) => (
              <React.Fragment key={index}>
                {index > 0 && <span className="breadcrumb-separator"> › </span>}
                <span 
                  className={`breadcrumb-segment ${index === labelParts.length - 1 ? 'active' : 'clickable'}`}
                  onClick={() => handleBreadcrumbClick(index)}
                >
                  {part}
                </span>
              </React.Fragment>
            ))}
          </div>
        </div>
        <button onClick={clearFilter} className="clear-filter-btn">× Clear Filter</button>
      </div>
    );
  };

  return (
    <div className="catalogue-container">
      {renderFilterBreadcrumb()}

      {displayMode === 'patents' && (
        <div className="catalogue-navigation-top">
          <button onClick={prevPage} disabled={currentPage === 0} className="btn btn-primary">
            ← Previous
          </button>
          <span className="page-indicator-top">
            Patent {currentPage + 1} of {totalPatents !== null ? totalPatents : displayPatents.length}
          </span>
          <button 
            onClick={nextPage} 
            disabled={totalPatents !== null ? currentPage >= totalPatents - 1 : currentPage >= displayPatents.length - 1}
            className="btn btn-primary"
          >
            Next →
          </button>
        </div>
      )}

      <div className="catalogue-controls">
        <div className="control-group">
          <button 
            onClick={() => setDisplayMode('toc')} 
            className={`btn ${displayMode === 'toc' ? 'btn-primary' : 'btn-secondary'}`}
          >
            Table of Contents
          </button>
          <button 
            onClick={() => setDisplayMode('index')} 
            className={`btn ${displayMode === 'index' ? 'btn-primary' : 'btn-secondary'}`}
          >
            Index
          </button>
          <button 
            onClick={() => setDisplayMode('patents')} 
            className={`btn ${displayMode === 'patents' ? 'btn-primary' : 'btn-secondary'}`}
          >
            View Patents
          </button>
        </div>

        {displayMode === 'patents' && (
          <div className="control-group">
            <button
              onClick={() => setViewMode('two-page')}
              className={`btn ${viewMode === 'two-page' ? 'btn-primary' : 'btn-secondary'}`}
            >
              Two-Page View
            </button>
            <button
              onClick={() => setViewMode('single-page')}
              className={`btn ${viewMode === 'single-page' ? 'btn-primary' : 'btn-secondary'}`}
            >
              Single-Page View
            </button>
          </div>
        )}
      </div>

      {displayMode === 'toc' && renderToc()}
      {displayMode === 'index' && renderIndexCards()}
      
      {displayMode === 'patents' && (
        <div className={bookContainerClasses}>
          {viewMode === 'two-page' ? (
            <div className="catalogue-spread">
              <PatentPage
                key={leftPatent ? leftPatent.patent_id : 'left-placeholder'}
                patent={leftPatent}
                position="left"
                viewMode="two-page"
                onNavigate={handleNavigateToPatent}
              />
              <PatentPage
                key={rightPatent ? rightPatent.patent_id : 'right-placeholder'}
                patent={rightPatent}
                position="right"
                viewMode="two-page"
                onNavigate={handleNavigateToPatent}
              />
            </div>
          ) : (
            <div className="catalogue-single-page-wrapper">
              <PatentPage
                key={currentPatent ? currentPatent.patent_id : 'single-placeholder'}
                patent={currentPatent}
                position="single"
                viewMode="single-page"
                onNavigate={handleNavigateToPatent}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Catalogue;
